import BaseEntity from '../BaseEntity';
import { isNumber } from 'lodash-es';
import ContourModel from '../2d/contourModel';
import { ENTITY_RENDER_ORDER, selectedOutlineColor } from '@/editor/constant/entity';
import { FaceOutlineModel } from '../3d/boundingBox/faceOutlineModel';
import { IShapePoint2d } from '@/editor/types/shape';

interface IRegionParams {
  contour: IShapePoint2d[];
  holes: IShapePoint2d[][];
  color: number;
  offGround: number;
}

/** 比较两点附带的圆弧数据：都不带圆弧，或圆心与绕向都相同 */
function isStrictEqualArc(point1: IShapePoint2d, point2: IShapePoint2d) {
  const { center: center1 } = point1;
  const { center: center2 } = point2;
  if (!center1 || !center2) return !center1 && !center2;
  return center1.x === center2.x && center1.y === center2.y && point1.clockwise === point2.clockwise;
}

function isStrictEqualPoint(point1: IShapePoint2d, point2: IShapePoint2d) {
  return point1.x === point2.x && point1.y === point2.y && isStrictEqualArc(point1, point2);
}

/** 逐点全等比较（不含浮点容差），用于判断分区形状是否变化、需要重建模型 */
function isStrictEqualPolygon(polygon1: IShapePoint2d[][], polygon2: IShapePoint2d[][]) {
  const points1 = polygon1.flat();
  const points2 = polygon2.flat();
  if (points1.length !== points2.length) return false;
  return points1.every((point, index) => isStrictEqualPoint(point, points2[index]));
}

export default class RegionSelectedEntity extends BaseEntity {
  protected override _model2d: ContourModel;
  protected override _model3d: FaceOutlineModel;
  private _params: IRegionParams = { offGround: 0, contour: [], holes: [], color: selectedOutlineColor };
  private _created: boolean = false;

  constructor(color: number) {
    super();

    this._model2d = new ContourModel();
    this._model2d.setDisplayOrder(ENTITY_RENDER_ORDER.renderOrderTemp);
    this._model3d = new FaceOutlineModel();
    this._model3d.setRenderOrder(ENTITY_RENDER_ORDER.renderOrderTemp);
    this._model3d.setColor(color);
  }

  get created() {
    return this._created;
  }

  override update(params: IRegionParams) {
    // let noDestroyUpdate = false;
    let needDestroyUpdate = !this._created || false;

    if (isNumber(params.offGround) && this._params.offGround !== params.offGround) {
      this._params.offGround = params.offGround;
      needDestroyUpdate = true;
    }
    if (
      params.contour &&
      params.holes &&
      !isStrictEqualPolygon([params.contour, ...params.holes], [this._params.contour, ...this._params.holes])
    ) {
      this._params.contour = params.contour;
      this._params.holes = params.holes;
      needDestroyUpdate = true;
    }
    if (params.color && this._params.color !== params.color) {
      this._params.color = params.color;
      needDestroyUpdate = true;
    }

    if (needDestroyUpdate) {
      this.clear();
      this._model2d.createModel({ ...this._params });
      this._model3d.createContourModel(this._params);
      this._created = true;
    }
  }

  clear() {
    this._model2d.clear();
    this._model3d.clearModel();
    this._created = false;
  }
}
